import type { RefineThemedLayoutHeaderProps } from "@refinedev/antd";
import { useThemedLayoutContext } from "@refinedev/antd";
import { useGetIdentity, useLogout } from "@refinedev/core";
import { LogoutOutlined, MenuOutlined, ReadOutlined } from "@ant-design/icons";
import {
  Avatar,
  Button,
  Grid,
  Layout as AntdLayout,
  Space,
  Switch,
  theme,
  Typography,
} from "antd";
import React, { use } from "react";
import { PlanTag } from "../plan-tag";
import { ColorModeContext } from "../../contexts/color-mode";
import type { User } from "../../types";

const { Text } = Typography;
const { useToken } = theme;

export const Header: React.FC<RefineThemedLayoutHeaderProps> = ({
  sticky = true,
}) => {
  const { token } = useToken();
  const { data: user } = useGetIdentity<User>();
  const { mode, setMode } = use(ColorModeContext);
  const { mobileSiderOpen, setMobileSiderOpen } = useThemedLayoutContext();
  const breakpoint = Grid.useBreakpoint();
  const isMobile = !breakpoint.lg;
  const { mutate: logout } = useLogout();

  const headerStyles: React.CSSProperties = {
    backgroundColor: token.colorBgElevated,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0px 24px",
    height: "64px",
  };

  if (sticky) {
    headerStyles.position = "sticky";
    headerStyles.top = 0;
    headerStyles.zIndex = 1;
  }

  return (
    <AntdLayout.Header style={headerStyles}>
      <div>
        {isMobile && (
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setMobileSiderOpen(!mobileSiderOpen)}
          />
        )}
      </div>
      <Space size="middle">
        <Button
          type="text"
          icon={<ReadOutlined />}
          href="https://docs.loopress.dev"
          target="_blank"
          rel="noopener noreferrer"
          title="Documentation"
        />
        <Switch
          checkedChildren="🌛"
          unCheckedChildren="🔆"
          onChange={() => setMode(mode === "light" ? "dark" : "light")}
          defaultChecked={mode === "dark"}
        />
        {user?.plan && <PlanTag plan={user.plan} />}
        {user?.email && !isMobile && <Text strong>{user.email}</Text>}
        <Avatar style={{ backgroundColor: token.colorPrimary }}>
          {user?.email?.[0]?.toUpperCase()}
        </Avatar>
        {!isMobile && (
          <Button
            type="text"
            icon={<LogoutOutlined />}
            onClick={() => logout()}
            title="Sign out"
          />
        )}
      </Space>
    </AntdLayout.Header>
  );
};
